import { observer } from 'mobx-react-lite';
import type { JSX } from 'react';
import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';

import type { IMailrailDripStep, MailrailDripEmailCampaign } from '@feathr/blackbox';
import { FilterCarousel, Label } from '@feathr/components';
import MailrailEmailDetails from '@feathr/extender/components/MailrailEmailDetails';
import ReportActions from '@feathr/extender/components/PageHeaderActions';
import { useReportExport } from '@feathr/extender/hooks';

import CampaignPage from '../../CampaignPage';
import CampaignReportSection, { getCampaignReportConfig } from '../CampaignReportSection';

import * as styles from './MailrailDripCampaignReportPage.css';

interface IProps {
  campaign: MailrailDripEmailCampaign;
}

interface IEmailEntry {
  /** The breadcrumb `mailing_id`: the campaign id for step 0, the step's `step_id` otherwise. */
  id: string;
  name: string;
  /** The embedded step; undefined for step 0 (the campaign's own email). */
  step?: IMailrailDripStep;
}

function MailrailDripCampaignReportPage({ campaign }: Readonly<IProps>): JSX.Element {
  const { t } = useTranslation();
  const [activeMailing, setActiveMailing] = useState<'all' | string>('all');
  const showAll = activeMailing === 'all';
  const shouldShowDetails = !showAll;
  const config = getCampaignReportConfig(campaign);
  const reportExportProps = useReportExport({ model: campaign, config });
  const name = campaign.get('name');

  const emails: IEmailEntry[] = [
    { id: campaign.id, name: campaign.get('enrollment_step_name'), step: undefined },
    ...campaign.get('steps').map((step) => ({ id: step.step_id, name: step.name, step })),
  ];

  const carouselItems = emails.map((email) => ({
    content: email.name,
    onClick: () => setActiveMailing(email.id),
  }));

  const carousel = (
    <div className={styles.carousel}>
      <FilterCarousel
        initialSlide={'prefix'}
        items={carouselItems}
        ordered={true}
        prefix={{
          content: t('All Emails'),
          onClick: () => setActiveMailing('all'),
        }}
      />
    </div>
  );

  const activeEmail = showAll ? undefined : emails.find((email) => email.id === activeMailing);
  const index = emails.findIndex((email) => email.id === activeMailing);

  const actions = showAll ? (
    <ReportActions campaign={campaign} {...reportExportProps} />
  ) : undefined;

  return (
    <CampaignPage actions={actions} campaign={campaign} headerChildren={carousel} title={name}>
      {shouldShowDetails && activeEmail && (
        <>
          <Label className={styles.label}>{`${index + 1}. ${activeEmail.name}`}</Label>
          <MailrailEmailDetails campaign={campaign} step={activeEmail.step} />
        </>
      )}
      <h3 className={shouldShowDetails ? styles.titleWithDetails : styles.title}>{t('Report')}</h3>
      <CampaignReportSection
        activeChannel={showAll ? undefined : activeMailing}
        campaign={campaign}
        {...reportExportProps}
      />
    </CampaignPage>
  );
}

export default observer(MailrailDripCampaignReportPage);
